import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
    ArrowDownTrayIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon,
    EllipsisVerticalIcon,
    PowerIcon,
    TrashIcon,
    ClipboardDocumentCheckIcon,
    ShieldCheckIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { SECTION_PERMISSIONS } from "@/config/permissions";
import { buildApprovalSubmissionFeedback } from "@/features/approvals/approval-utils";
import { usePermissions } from "@/features/auth/usePermissions";
import { extractApiError } from "@/lib/api-error";
import {
    averageBy,
    buildBreakdownRows,
    collectPaginatedRows,
    countBy,
    createExportFileName,
    exportRide2FiveExcelReport,
} from "@/lib/export/excel";
import { activateDriver, deactivateDriver, deleteDriver, getDrivers } from "../api/driversApi";
import type { DriversQueryParams, DriverStatus } from "../types";

const statusOptions: { value: DriverStatus | ""; label: string }[] = [
    { value: "", label: "All Status" },
    { value: "ACTIVE", label: "Active" },
    { value: "INACTIVE", label: "Inactive" },
    { value: "SUSPENDED", label: "Suspended" },
    { value: "DELETED", label: "Deleted" }
];

export const DriversRoute = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { hasPermission } = usePermissions();
    const canViewApprovals = hasPermission(SECTION_PERMISSIONS.APPROVALS[0]);
    const canExportDrivers = hasPermission(SECTION_PERMISSIONS.DRIVERS_EXPORT[0]);
    const canModerateDrivers = hasPermission(SECTION_PERMISSIONS.DRIVERS_MODERATE[0]);
    const [isExporting, setIsExporting] = useState(false);
    const [exportError, setExportError] = useState("");
    const [filters, setFilters] = useState<DriversQueryParams>({
        page: 0,
        size: 20,
        status: "",
        search: "",
        emailVerified: "",
        phoneVerified: "",
        ninVerified: "",
        createdFrom: "",
        createdTo: ""
    });
    const [showFilters, setShowFilters] = useState(false);
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<{ userId: string; name: string } | null>(null);
    const [deleteReason, setDeleteReason] = useState("");
    const [feedback, setFeedback] = useState<{
        type: "success" | "error";
        text: string;
    } | null>(null);
    const menuRefs = useRef<Record<string, HTMLDivElement | null>>({});

    const { data, isLoading, error } = useQuery({
        queryKey: ["drivers", filters],
        queryFn: () => getDrivers(filters)
    });

    const activateMutation = useMutation({
        mutationFn: (userId: string) => activateDriver(userId),
        onError: (error) => {
            setFeedback({ type: "error", text: extractApiError(error) });
        },
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: ["drivers"] });
            queryClient.invalidateQueries({ queryKey: ["userStats"] });
            setOpenMenuId(null);
            setFeedback({
                type: "success",
                text: buildApprovalSubmissionFeedback(response),
            });
        }
    });

    const deactivateMutation = useMutation({
        mutationFn: (userId: string) => deactivateDriver(userId),
        onError: (error) => {
            setFeedback({ type: "error", text: extractApiError(error) });
        },
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: ["drivers"] });
            queryClient.invalidateQueries({ queryKey: ["userStats"] });
            setOpenMenuId(null);
            setFeedback({
                type: "success",
                text: buildApprovalSubmissionFeedback(response),
            });
        }
    });

    const deleteMutation = useMutation({
        mutationFn: ({ userId, reason }: { userId: string; reason?: string }) =>
            deleteDriver(userId, reason),
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: ["drivers"] });
            setDeleteConfirm(null);
            setDeleteReason("");
            setOpenMenuId(null);
            setFeedback({
                type: "success",
                text: buildApprovalSubmissionFeedback(response),
            });
        },
        onError: (error) => {
            setFeedback({ type: "error", text: extractApiError(error) });
        }
    });

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (openMenuId && menuRefs.current[openMenuId]) {
                if (!menuRefs.current[openMenuId]?.contains(event.target as Node)) {
                    setOpenMenuId(null);
                }
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [openMenuId]);

    useEffect(() => {
        if (!feedback) return;

        const timeoutId = window.setTimeout(() => setFeedback(null), 6000);
        return () => window.clearTimeout(timeoutId);
    }, [feedback]);

    const handleFilterChange = (key: keyof DriversQueryParams, value: string | number | boolean) => {
        setFilters((prev) => ({ ...prev, [key]: value, page: 0 }));
    };

    const handlePageChange = (newPage: number) => {
        setFilters((prev) => ({ ...prev, page: newPage }));
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return "Never";
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    const handleExport = async () => {
        try {
            setIsExporting(true);
            setExportError("");

            const drivers = await collectPaginatedRows({
                fetchPage: (page) =>
                    getDrivers({
                        ...filters,
                        page,
                        size: 100,
                    }),
                getRows: (response) => response.data.drivers,
                getTotalPages: (response) => response.data.totalPages,
            });

            const statusCounts = countBy(drivers, (driver) => driver.status);
            const emailVerifiedCount = drivers.filter((driver) => driver.emailVerified).length;
            const phoneVerifiedCount = drivers.filter((driver) => driver.phoneVerified).length;
            const ninVerifiedCount = drivers.filter((driver) => driver.ninVerified).length;
            const activeCount = drivers.filter((driver) => driver.active).length;
            const averageProfileCompletion =
                averageBy(drivers, (driver) => driver.profileCompletionPercentage) ?? 0;

            await exportRide2FiveExcelReport({
                title: "Drivers Export",
                subtitle:
                    "Filtered driver directory export with verification and activity coverage.",
                fileName: createExportFileName("drivers_export"),
                filters,
                rows: drivers,
                metrics: [
                    { label: "Filtered Drivers", value: drivers.length },
                    {
                        label: "Active Drivers",
                        value: activeCount,
                        note: `${drivers.length ? ((activeCount / drivers.length) * 100).toFixed(1) : "0.0"}% of export`,
                    },
                    {
                        label: "Average Profile Completion",
                        value: `${averageProfileCompletion.toFixed(1)}%`,
                    },
                    {
                        label: "Logged In At Least Once",
                        value: drivers.filter((driver) => Boolean(driver.lastLoginAt)).length,
                    },
                ],
                sections: [
                    {
                        title: "Status Breakdown",
                        rows: buildBreakdownRows(statusCounts),
                    },
                    {
                        title: "Verification Coverage",
                        rows: [
                            { label: "Email Verified", value: emailVerifiedCount },
                            { label: "Phone Verified", value: phoneVerifiedCount },
                            { label: "Driver Licence / NIN Verified", value: ninVerifiedCount },
                        ],
                    },
                ],
                columns: [
                    { header: "User ID", width: 38, value: (driver) => driver.userId },
                    { header: "First Name", width: 18, value: (driver) => driver.firstName },
                    { header: "Last Name", width: 18, value: (driver) => driver.lastName },
                    { header: "Email", width: 28, value: (driver) => driver.email },
                    { header: "Phone", width: 18, value: (driver) => driver.phoneNumber },
                    { header: "Status", width: 16, value: (driver) => driver.status },
                    {
                        header: "Email Verified",
                        width: 14,
                        alignment: "center",
                        value: (driver) => (driver.emailVerified ? "Yes" : "No"),
                    },
                    {
                        header: "Phone Verified",
                        width: 14,
                        alignment: "center",
                        value: (driver) => (driver.phoneVerified ? "Yes" : "No"),
                    },
                    {
                        header: "NIN Verified",
                        width: 14,
                        alignment: "center",
                        value: (driver) => (driver.ninVerified ? "Yes" : "No"),
                    },
                    {
                        header: "Profile Completion %",
                        width: 20,
                        alignment: "right",
                        value: (driver) => driver.profileCompletionPercentage,
                    },
                    {
                        header: "Last Login",
                        width: 22,
                        value: (driver) =>
                            driver.lastLoginAt ? formatDate(driver.lastLoginAt) : "Never",
                    },
                    {
                        header: "Joined",
                        width: 22,
                        value: (driver) => formatDate(driver.createdAt),
                    },
                    {
                        header: "Deleted At",
                        width: 22,
                        value: (driver) =>
                            driver.deletedAt ? formatDate(driver.deletedAt) : "—",
                    },
                ],
                dataSheetName: "Drivers",
            });
        } catch (error) {
            setExportError(extractApiError(error));
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="space-y-6">
            <section>
                <h1 className="font-display text-3xl font-bold text-slate-900 dark:text-slate-50">
                    Drivers Management
                </h1>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">
                    View and manage all registered drivers on the platform.
                </p>
            </section>

            {exportError ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300">
                    {exportError}
                </div>
            ) : null}
            {feedback ? (
                <div
                    className={`rounded-2xl border px-4 py-3 text-sm ${
                        feedback.type === "success"
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-900/20 dark:text-emerald-300"
                            : "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/60 dark:bg-rose-900/20 dark:text-rose-300"
                    }`}
                >
                    {feedback.text}
                </div>
            ) : null}

            {canViewApprovals ? (
                <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                                Approvals
                            </h2>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                Review driver document approvals that still live under this
                                section.
                            </p>
                        </div>
                        <div className="flex flex-col gap-3 sm:flex-row">
                            <Button
                                variant="outline"
                                onClick={() => navigate("/approvals/driver-licences")}
                                className="flex items-center gap-2"
                            >
                                <ClipboardDocumentCheckIcon className="h-4 w-4" />
                                Driver Licences
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => navigate("/approvals/road-worthiness")}
                                className="flex items-center gap-2"
                            >
                                <ShieldCheckIcon className="h-4 w-4" />
                                Road Worthiness
                            </Button>
                        </div>
                    </div>
                </section>
            ) : null}

            {/* Search and Filters */}
            <section className="space-y-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="relative flex-1 max-w-md">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Search by name, email, or phone..."
                            value={filters.search || ""}
                            onChange={(e) => handleFilterChange("search", e.target.value)}
                            className="w-full rounded-xl border border-slate-200 bg-white pl-10 pr-4 py-2.5 text-sm text-slate-900 shadow-sm transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:border-primary-500"
                        />
                    </div>
                    <Button
                        variant="outline"
                        onClick={() => setShowFilters(!showFilters)}
                        className="flex items-center gap-2"
                    >
                        <FunnelIcon className="h-4 w-4" />
                        Filters
                    </Button>
                    {canExportDrivers ? (
                        <Button
                            onClick={handleExport}
                            disabled={isExporting}
                            className="flex items-center gap-2"
                        >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            {isExporting ? "Exporting..." : "Export Excel"}
                        </Button>
                    ) : null}
                </div>

                {/* Advanced Filters */}
                {showFilters && (
                    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Status
                                </label>
                                <select
                                    value={filters.status || ""}
                                    onChange={(e) => handleFilterChange("status", e.target.value)}
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                >
                                    {statusOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Email Verified
                                </label>
                                <select
                                    value={filters.emailVerified === "" ? "" : filters.emailVerified ? "true" : "false"}
                                    onChange={(e) =>
                                        handleFilterChange("emailVerified", e.target.value === "" ? "" : e.target.value === "true")
                                    }
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                >
                                    <option value="">All</option>
                                    <option value="true">Verified</option>
                                    <option value="false">Not Verified</option>
                                </select>
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Phone Verified
                                </label>
                                <select
                                    value={filters.phoneVerified === "" ? "" : filters.phoneVerified ? "true" : "false"}
                                    onChange={(e) =>
                                        handleFilterChange("phoneVerified", e.target.value === "" ? "" : e.target.value === "true")
                                    }
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                >
                                    <option value="">All</option>
                                    <option value="true">Verified</option>
                                    <option value="false">Not Verified</option>
                                </select>
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    NIN Verified
                                </label>
                                <select
                                    value={filters.ninVerified === "" ? "" : filters.ninVerified ? "true" : "false"}
                                    onChange={(e) =>
                                        handleFilterChange("ninVerified", e.target.value === "" ? "" : e.target.value === "true")
                                    }
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                >
                                    <option value="">All</option>
                                    <option value="true">Verified</option>
                                    <option value="false">Not Verified</option>
                                </select>
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Created From
                                </label>
                                <input
                                    type="date"
                                    value={filters.createdFrom || ""}
                                    onChange={(e) => handleFilterChange("createdFrom", e.target.value)}
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                />
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Created To
                                </label>
                                <input
                                    type="date"
                                    value={filters.createdTo || ""}
                                    onChange={(e) => handleFilterChange("createdTo", e.target.value)}
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                />
                            </div>
                        </div>
                    </div>
                )}
            </section>

            {/* Drivers Table */}
            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                {isLoading ? (
                    <div className="flex h-64 items-center justify-center">
                        <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
                    </div>
                ) : error ? (
                    <div className="flex h-64 items-center justify-center text-rose-500">
                        <p>Failed to load drivers. Please try again.</p>
                    </div>
                ) : !data?.data?.drivers.length ? (
                    <div className="flex h-64 items-center justify-center text-slate-500">
                        <p>No drivers found.</p>
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                                <thead className="bg-slate-50 dark:bg-slate-800/60">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                            Driver
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                            Contact
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                            Verification
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                            Profile
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                            Last Login
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                            Joined
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 bg-white dark:divide-slate-800 dark:bg-slate-900">
                                    {data.data.drivers.map((driver) => (
                                        <tr
                                            key={driver.userId}
                                            onClick={() => navigate(`/drivers/${driver.userId}`)}
                                            className="cursor-pointer transition hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                        >
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <div className="flex items-center">
                                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
                                                        {driver.firstName[0]}
                                                        {driver.lastName[0]}
                                                    </div>
                                                    <div className="ml-4">
                                                        <div className="text-sm font-medium text-slate-900 dark:text-slate-50">
                                                            {driver.firstName} {driver.lastName}
                                                        </div>
                                                        <div className="text-xs text-slate-500 dark:text-slate-400">
                                                            {driver.userId.slice(0, 8)}...
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <div className="text-sm text-slate-900 dark:text-slate-50">{driver.email}</div>
                                                <div className="text-xs text-slate-500 dark:text-slate-400">{driver.phoneNumber}</div>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <div className="flex flex-col gap-1">
                                                    <div className="flex items-center gap-1.5 text-xs">
                                                        {driver.emailVerified ? (
                                                            <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
                                                        ) : (
                                                            <XCircleIcon className="h-4 w-4 text-slate-400" />
                                                        )}
                                                        <span className="text-slate-600 dark:text-slate-300">Email</span>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 text-xs">
                                                        {driver.phoneVerified ? (
                                                            <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
                                                        ) : (
                                                            <XCircleIcon className="h-4 w-4 text-slate-400" />
                                                        )}
                                                        <span className="text-slate-600 dark:text-slate-300">Phone</span>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 text-xs">
                                                        {driver.ninVerified ? (
                                                            <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
                                                        ) : (
                                                            <XCircleIcon className="h-4 w-4 text-slate-400" />
                                                        )}
                                                        <span className="text-slate-600 dark:text-slate-300">NIN</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <span
                                                    className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                                        driver.status === "ACTIVE"
                                                            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                                                            : driver.status === "SUSPENDED"
                                                              ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                                                              : driver.status === "INACTIVE"
                                                                ? "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                                                : "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400"
                                                    }`}
                                                >
                                                    {driver.status}
                                                </span>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="h-2 w-24 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                                                        <div
                                                            className="h-full bg-primary-500"
                                                            style={{ width: `${driver.profileCompletionPercentage}%` }}
                                                        />
                                                    </div>
                                                    <span className="text-xs font-medium text-slate-600 dark:text-slate-300">
                                                        {driver.profileCompletionPercentage}%
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-500 dark:text-slate-400">
                                                {driver.lastLoginAt ? (
                                                    <div className="flex items-center gap-1.5">
                                                        <ClockIcon className="h-4 w-4" />
                                                        {formatDate(driver.lastLoginAt)}
                                                    </div>
                                                ) : (
                                                    <span className="text-slate-400">Never</span>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-500 dark:text-slate-400">
                                                {formatDate(driver.createdAt)}
                                            </td>
                                            <td
                                                className="whitespace-nowrap px-6 py-4 text-right"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {canModerateDrivers ? (
                                                    <div className="relative" ref={(el) => (menuRefs.current[driver.userId] = el)}>
                                                        <button
                                                            onClick={() => setOpenMenuId(openMenuId === driver.userId ? null : driver.userId)}
                                                            className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
                                                            aria-label="Actions"
                                                        >
                                                            <EllipsisVerticalIcon className="h-5 w-5" />
                                                        </button>
                                                        {openMenuId === driver.userId && (
                                                            <div className="absolute right-0 z-10 mt-2 w-48 rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800">
                                                                <div className="py-1">
                                                                    {driver.status === "ACTIVE" ? (
                                                                        <button
                                                                            onClick={() => {
                                                                                deactivateMutation.mutate(driver.userId);
                                                                            }}
                                                                            disabled={deactivateMutation.isPending}
                                                                            className="flex w-full items-center gap-2 px-4 py-2 text-sm text-slate-700 transition hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700 disabled:opacity-50"
                                                                        >
                                                                            <PowerIcon className="h-4 w-4 text-amber-500" />
                                                                            Deactivate
                                                                        </button>
                                                                    ) : (
                                                                        <button
                                                                            onClick={() => {
                                                                                activateMutation.mutate(driver.userId);
                                                                            }}
                                                                            disabled={activateMutation.isPending}
                                                                            className="flex w-full items-center gap-2 px-4 py-2 text-sm text-slate-700 transition hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700 disabled:opacity-50"
                                                                        >
                                                                            <PowerIcon className="h-4 w-4 text-emerald-500" />
                                                                            Activate
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        onClick={() => {
                                                                            setDeleteConfirm({
                                                                                userId: driver.userId,
                                                                                name: `${driver.firstName} ${driver.lastName}`
                                                                            });
                                                                            setDeleteReason("");
                                                                            setOpenMenuId(null);
                                                                        }}
                                                                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-rose-600 transition hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-900/20"
                                                                    >
                                                                        <TrashIcon className="h-4 w-4" />
                                                                        Delete Request
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : null}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {data.data.totalPages > 1 && (
                            <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4 dark:border-slate-800">
                                <div className="text-sm text-slate-500 dark:text-slate-400">
                                    Showing <span className="font-medium text-slate-900 dark:text-slate-50">
                                        {data.data.page * data.data.size + 1}
                                    </span>{" "}
                                    to{" "}
                                    <span className="font-medium text-slate-900 dark:text-slate-50">
                                        {Math.min((data.data.page + 1) * data.data.size, data.data.totalElements)}
                                    </span>{" "}
                                    of <span className="font-medium text-slate-900 dark:text-slate-50">{data.data.totalElements}</span>{" "}
                                    drivers
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handlePageChange(data.data.page - 1)}
                                        disabled={!data.data.hasPrevious}
                                    >
                                        Previous
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handlePageChange(data.data.page + 1)}
                                        disabled={!data.data.hasNext}
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </section>

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-700 dark:bg-slate-800">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">Delete Driver</h3>
                        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                            This will submit a delete request for <strong>{deleteConfirm.name}</strong>{" "}
                            to the super admin approval queue. The driver will only be deleted after
                            approval.
                        </p>
                        <textarea
                            rows={3}
                            value={deleteReason}
                            onChange={(event) => setDeleteReason(event.target.value)}
                            placeholder="Optional reason for deleting this driver..."
                            className="mt-4 w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                        />
                        <div className="mt-6 flex gap-3">
                            <Button
                                variant="outline"
                                className="flex-1"
                                onClick={() => {
                                    setDeleteConfirm(null);
                                    setDeleteReason("");
                                }}
                                disabled={deleteMutation.isPending}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                className="flex-1 bg-rose-600 hover:bg-rose-700"
                                onClick={() =>
                                    deleteMutation.mutate({
                                        userId: deleteConfirm.userId,
                                        reason: deleteReason.trim() || undefined,
                                    })
                                }
                                disabled={deleteMutation.isPending}
                            >
                                {deleteMutation.isPending
                                    ? "Submitting..."
                                    : "Submit Delete Request"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
